"use client";

import { getApiBaseUrl } from "@/lib/api/base-url";
import { parseApiError } from "@/lib/api/error";
import type {
  ProfileImportCreateResponse,
} from "@/lib/api/model";

export interface UploadImportOptions {
  jobId: string;
  stageId: string;
  file: File;
  onProgress?: (percent: number) => void;
  signal?: AbortSignal;
}

/**
 * Upload a single file to `/api/jobs/{jobId}/imports` using XHR so we can
 * report per-file upload progress. `customInstance` uses `fetch`, which does
 * not expose upload progress, so this helper bypasses Orval/fetch for this
 * one path. Backend contract is unchanged — one multipart POST per file.
 */
export function uploadImportFile({
  jobId,
  stageId,
  file,
  onProgress,
  signal,
}: UploadImportOptions): Promise<ProfileImportCreateResponse> {
  return new Promise<ProfileImportCreateResponse>((resolve, reject) => {
    const form = new FormData();
    form.append("stage_id", stageId);
    form.append("files", file);

    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${getApiBaseUrl()}/api/jobs/${jobId}/imports`);
    // Cookie auth: cookies are HttpOnly and the browser attaches them
    // automatically to cross-origin requests when withCredentials is set.
    xhr.withCredentials = true;

    xhr.upload.onprogress = (evt) => {
      if (!evt.lengthComputable || !onProgress) return;
      // Report true byte progress (0-100). Small files may jump from 0 to
      // 100 in a single event — the caller distinguishes "bytes complete
      // but server hasn't ACKed yet" via the `state` field, not by
      // clamping progress below 100.
      const pct = Math.round((evt.loaded / evt.total) * 100);
      onProgress(pct);
    };

    xhr.onload = () => {
      let body: unknown = null;
      try {
        body = xhr.responseText ? JSON.parse(xhr.responseText) : null;
      } catch {
        body = null;
      }

      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(100);
        const payload =
          body && typeof body === "object" && "data" in (body as object)
            ? (body as { data: ProfileImportCreateResponse }).data
            : (body as ProfileImportCreateResponse);
        resolve(payload);
        return;
      }
      reject(parseApiError(body ?? {}, xhr.status));
    };

    xhr.onerror = () => {
      reject(new Error("Network error while uploading file"));
    };

    xhr.onabort = () => {
      reject(new DOMException("Upload aborted", "AbortError"));
    };

    if (signal) {
      if (signal.aborted) {
        xhr.abort();
        return;
      }
      signal.addEventListener("abort", () => xhr.abort(), { once: true });
    }

    xhr.send(form);
  });
}
